/**
 * The rules that make a stolen session cookie worth less, and a signed-out one
 * worth nothing. ADR-038 has the reasoning; this file is the policy, kept free
 * of databases and clocks so every rule can be tested directly.
 *
 * `sessions.ts` still owns how long a session lasts from the moment it starts
 * (twelve hours, or thirty days when somebody ticks "keep me signed in"). What
 * is here is everything layered on top:
 *
 *  - an idle limit, so a session nobody is using stops being a credential;
 *  - shorter limits for an account that can administer the system;
 *  - rotation of the cookie's value, so a copy taken at one moment stops
 *    working shortly afterwards, and using an old copy is noticed;
 *  - the device check: the browser a session was opened in, recognised loosely
 *    enough that an automatic browser update, or switching a phone to "desktop
 *    site", does not sign anybody out.
 */
import { REMEMBERED_DAYS, SESSION_HOURS } from './sessions.js';
const MINUTE_MS = 60_000;
/**
 * How long a session may go unused before it ends.
 *
 * ## Why there is one again, and why it is not ninety minutes
 *
 * `sessions.ts` records that a ninety-minute idle timeout was tried and
 * removed: a dispatcher on the phone all morning was being signed out four
 * times a day. That reasoning still holds and these numbers respect it.
 *
 * "Idle" here means *no request at all from that session* — and an open
 * console is not idle by that measure, because the notification tray asks the
 * server for news every minute while the tab is on screen. So the clock only
 * runs when the browser is closed, the laptop is asleep or the tab has been in
 * the background for hours. Four hours of that is a lunch break and a long
 * meeting with room to spare; it is not an ordinary working gap.
 *
 * What it buys: a cookie copied off a machine that has since been shut for the
 * evening stops working within hours instead of lasting until the next
 * morning — or for thirty days, on a remembered session.
 */
export const IDLE_MINUTES = {
    standard: 4 * 60,
    remembered: 7 * 24 * 60,
};
/**
 * The same, for an account that can manage users and settings.
 *
 * An admin's session is the one an attacker most wants: it can create accounts,
 * reset passwords and read every customer. So it is held to a tighter idle
 * limit and a shorter "remembered" life, while the working-day session stays
 * twelve hours — an admin here is also somebody doing a day's dispatching, and
 * a session that ends at two in the afternoon is one that teaches people to
 * write their password down.
 */
export const PRIVILEGED_IDLE_MINUTES = {
    standard: 2 * 60,
    remembered: 24 * 60,
};
/** How long "keep me signed in" lasts on an admin account. */
export const PRIVILEGED_REMEMBERED_DAYS = 7;
/**
 * How old a session cookie's value may get before the server issues a new one.
 *
 * The session stays the same — same row, same deadline, nothing for the person
 * to notice. Only the secret in the cookie changes. A copy of the cookie taken
 * now is useless once the real browser has moved on to its successor, and
 * presenting it after that is treated as theft (see `ROTATION_GRACE_SECONDS`).
 */
export const ROTATE_AFTER_MINUTES = 15;
/**
 * How long an old cookie value is still accepted after its successor is used.
 *
 * Several requests leave a busy page at once, and some were already in flight
 * with the old value when the new one arrived. The slowest of them matter
 * most: a technician's photo upload on a weak signal can take a minute or two
 * to arrive, and the session is checked when it *finishes* arriving, not when
 * it started. Five minutes covers that with room; after it, the old value
 * turning up again means two holders.
 *
 * (Was thirty seconds. Long enough for a fetch on a desk, not for an upload on
 * a hillside.)
 */
export const ROTATION_GRACE_SECONDS = 5 * 60;
/** Whether this set of roles can administer the system. */
export function isPrivileged(roles) {
    return roles.includes('admin');
}
/** The limits a session is held to. */
export function sessionLimits(remembered, privileged) {
    if (privileged) {
        return {
            absoluteMs: remembered ? PRIVILEGED_REMEMBERED_DAYS * 24 * 60 * MINUTE_MS : SESSION_HOURS * 60 * MINUTE_MS,
            idleMs: (remembered ? PRIVILEGED_IDLE_MINUTES.remembered : PRIVILEGED_IDLE_MINUTES.standard) * MINUTE_MS,
        };
    }
    return {
        absoluteMs: remembered ? REMEMBERED_DAYS * 24 * 60 * MINUTE_MS : SESSION_HOURS * 60 * MINUTE_MS,
        idleMs: (remembered ? IDLE_MINUTES.remembered : IDLE_MINUTES.standard) * MINUTE_MS,
    };
}
/** When a session opened now ends, absolutely. */
export function sessionDeadline(from, options) {
    return new Date(from.getTime() + sessionLimits(options.remembered, options.privileged).absoluteMs);
}
/**
 * Whether a session may still be used.
 *
 * The absolute limit is applied twice on purpose: once as the stored
 * `expiresAt`, and again from `capsFrom` (normally `createdAt`) against the
 * limits for the account *as it is now*. The second is what holds an account promoted to admin to
 * the admin limits without a migration of every row it already holds.
 */
export function sessionVerdict(session, privileged, now = new Date()) {
    const limits = sessionLimits(session.remembered, privileged);
    const endsAt = new Date(Math.min(session.expiresAt.getTime(), (session.capsFrom ?? session.createdAt).getTime() + limits.absoluteMs));
    if (endsAt.getTime() <= now.getTime())
        return { state: 'expired' };
    const idleEndsAt = new Date(session.lastSeenAt.getTime() + limits.idleMs);
    if (idleEndsAt.getTime() <= now.getTime())
        return { state: 'idle' };
    return { state: 'live', endsAt, idleEndsAt };
}
/** Whether the cookie's value is old enough to be replaced. */
export function rotationDue(rotatedAt, now = new Date()) {
    return now.getTime() - rotatedAt.getTime() >= ROTATE_AFTER_MINUTES * MINUTE_MS;
}
/** Whether an old value is still inside its in-flight allowance. */
export function withinRotationGrace(retiredAt, now = new Date()) {
    return now.getTime() - retiredAt.getTime() <= ROTATION_GRACE_SECONDS * 1000;
}
/**
 * A browser's identity with the version numbers taken out.
 *
 * Kept for logs and for anybody comparing raw strings. The session check uses
 * `browserFamily`, which is looser on purpose.
 */
export function normaliseUserAgent(userAgent) {
    if (!userAgent)
        return null;
    return userAgent
        .slice(0, 300)
        .toLowerCase()
        .replace(/\d+(?:[._]\d+)*/g, '#')
        .replace(/\s+/g, ' ')
        .trim();
}
/** Which browser, in words. */
function browserName(ua) {
    if (ua.startsWith('dart/') || ua.includes('dart:io'))
        return 'GoSpeedNet phone app';
    if (ua.includes('edg/') || ua.includes('edga/') || ua.includes('edgios/'))
        return 'Edge';
    if (ua.includes('opr/') || ua.includes('opera'))
        return 'Opera';
    if (ua.includes('samsungbrowser'))
        return 'Samsung Internet';
    if (ua.includes('firefox/') || ua.includes('fxios'))
        return 'Firefox';
    if (ua.includes('chrome/') || ua.includes('crios'))
        return 'Chrome';
    if (ua.includes('safari/'))
        return 'Safari';
    if (ua.includes('node') || ua.includes('curl'))
        return 'A script';
    return 'A browser';
}
/** Which operating system, in words, or null when it cannot be told. */
function systemName(ua) {
    if (ua.includes('android'))
        return 'Android';
    if (ua.includes('iphone'))
        return 'iPhone';
    if (ua.includes('ipad'))
        return 'iPad';
    if (ua.includes('windows'))
        return 'Windows';
    if (ua.includes('mac os') || ua.includes('macintosh'))
        return 'Mac';
    if (ua.includes('cros'))
        return 'Chromebook';
    if (ua.includes('linux'))
        return 'Linux';
    return null;
}
/**
 * The part of a browser's identity a session is held to.
 *
 * Browser family, and the operating system *grouped so that a phone's "desktop
 * site" switch does not change it*: Chrome on Android asking for the desktop
 * site says "Linux", and Safari on an iPhone says "Mac". So Android, Linux and
 * ChromeOS are one group, and iPhone, iPad and Mac another. Version numbers and
 * the mobile/desktop distinction are ignored entirely.
 *
 * What it still notices is what matters: a session cookie from a Windows
 * laptop turning up in a browser on an Android phone, or in Firefox when it
 * was opened in Chrome. A thief who copies the user-agent as well passes it;
 * the device cookie and rotation are the other two checks, and none of the
 * three is relied on alone.
 */
export function browserFamily(userAgent) {
    if (!userAgent)
        return null;
    const ua = userAgent.slice(0, 300).toLowerCase();
    const system = systemName(ua);
    const group = system === 'Android' || system === 'Linux' || system === 'Chromebook'
        ? 'linux'
        : system === 'iPhone' || system === 'iPad' || system === 'Mac'
            ? 'apple'
            : system === 'Windows'
                ? 'windows'
                : 'other';
    return `${browserName(ua)}|${group}`;
}
/**
 * Whether a request comes from the browser the session was opened in.
 *
 * A session recorded without a user-agent (a script, an old row) is not held
 * to one: there is nothing to compare, and refusing would sign out a client
 * that never sent one in the first place.
 */
export function sameBrowser(recorded, presented) {
    const before = browserFamily(recorded);
    if (before === null)
        return true;
    return before === browserFamily(presented);
}
/**
 * A plain description of a device, for the list of places somebody is signed in.
 *
 * Deliberately coarse. "Chrome on Android" is what a person recognises as
 * their phone; a full user-agent string is not, and neither is an IP address on
 * a mobile network that changes every hour.
 */
export function describeDevice(userAgent) {
    if (!userAgent)
        return 'Unknown device';
    const ua = userAgent.toLowerCase();
    const browser = browserName(ua);
    if (browser === 'GoSpeedNet phone app')
        return browser;
    const system = systemName(ua);
    return system ? `${browser} on ${system}` : browser;
}
/**
 * Why a session ended, as stored on the row and shown in the audit log.
 *
 * A closed list so the audit screen and any alerting can rely on the values.
 */
export const SESSION_END_REASONS = [
    'signed_out',
    'signed_out_elsewhere',
    'replaced',
    'password_changed',
    'password_reset',
    'role_changed',
    'deactivated',
    'ended_by_admin',
    'idle',
    'expired',
    'token_reuse',
    'device_mismatch',
];
/** What somebody is told on the sign-in screen when the server ended their session. */
export function sessionEndedMessage(reason) {
    switch (reason) {
        case 'idle':
            return 'You were signed out because this device had not been used for a while. Sign in again to carry on.';
        case 'expired':
            return 'Your session reached its time limit, so you were signed out. Sign in again to carry on.';
        case 'token_reuse':
        case 'device_mismatch':
            return ('You were signed out to keep your account safe: your sign-in was used from somewhere ' +
                'that did not look like this device. Sign in again. If this keeps happening, change your ' +
                'password and tell an admin.');
        case 'password_changed':
        case 'password_reset':
            return 'Your password was changed, so this device was signed out. Sign in with the new one.';
        case 'role_changed':
            return 'What your account can do was changed, so you were signed out. Sign in again to carry on.';
        case 'ended_by_admin':
        case 'signed_out_elsewhere':
            return 'This device was signed out from another session. Sign in again to carry on.';
        default:
            return 'Your session has ended. Sign in again to carry on.';
    }
}
//# sourceMappingURL=session-security.js.map